/**
 * Camera math for keeping a restaurant's pin visible above the map's
 * selected-restaurant card.
 *
 * The card floats over the bottom of the map, so the geometric map center is
 * often behind it. To center a pin in the part of the map that is actually
 * visible, the camera is eased with a bottom `padding` equal to the card's
 * covered height plus a gap: Mapbox then treats the middle of the padded area
 * as the center. The padding is applied per move with `retainPadding: false`
 * (documented in mapbox-gl's CameraOptions), so it is never stored on the map:
 * a later member drag, or a search that reads the map center, sees an ordinary
 * unpadded camera.
 *
 * Everything here is pure; the DOM measuring lives in Search.tsx.
 */
import { getPanToDuration } from './moveGesture';

/** Mapbox `PaddingOptions`: pixels reserved on each side of the viewport. */
export interface CameraPadding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** The part of a DOMRect these helpers read: viewport-relative edges. */
export interface EdgeRect {
  top: number;
  bottom: number;
}

/** Breathing room between the pin and the top of the card. */
export const CARD_CLEARANCE_GAP_PX = 20;
/**
 * Covered height assumed when the card could not be measured: the card's usual
 * 120px bottom offset plus a typical ~400px card. Used only as a fallback.
 */
export const DEFAULT_CARD_COVERED_PX = 520;
/** However tall the card is, never reserve so much that less than this stays visible. */
export const MIN_VISIBLE_HEIGHT_PX = 160;
/**
 * How far a pin must sit from the edge of the visible area to count as
 * comfortably visible. The pin icon is ~45px tall and drawn around its
 * coordinate, so this keeps most of it clear of the card and the map edges.
 */
export const PIN_VISIBILITY_MARGIN_PX = 30;

/**
 * Height of the map covered by the card, from the map container's bottom edge
 * up to the card's top edge. Measured from real rects, so it already includes
 * the card's height, its bottom offset and any safe-area inset. A missing or
 * non-finite measurement falls back to DEFAULT_CARD_COVERED_PX; a card that
 * does not reach the map covers nothing.
 */
export function getCardCoveredHeight(mapRect: EdgeRect, cardRect: EdgeRect | null | undefined): number {
  if (!cardRect) return DEFAULT_CARD_COVERED_PX;
  const covered = mapRect.bottom - cardRect.top;
  if (!Number.isFinite(covered)) return DEFAULT_CARD_COVERED_PX;
  return Math.max(0, covered);
}

/**
 * Camera padding that makes the middle of the area above the card the center:
 * bottom = covered height + gap, clamped so at least MIN_VISIBLE_HEIGHT_PX of
 * the map stays visible (very short screens). `topInset` is for an overlay
 * inside the map container at the top; the search header is outside it.
 */
export function getVisibleAreaPadding({
  mapRect,
  cardRect,
  gap = CARD_CLEARANCE_GAP_PX,
  topInset = 0,
}: {
  mapRect: EdgeRect;
  cardRect: EdgeRect | null | undefined;
  gap?: number;
  topInset?: number;
}): CameraPadding {
  const mapHeight = mapRect.bottom - mapRect.top;
  const maxBottom = Math.max(0, mapHeight - topInset - MIN_VISIBLE_HEIGHT_PX);
  const bottom = Math.min(getCardCoveredHeight(mapRect, cardRect) + gap, maxBottom);
  return { top: topInset, right: 0, bottom, left: 0 };
}

/** Where, as a fraction of the map's height from its top, padded centering puts the pin. */
export function getVisibleCenterFraction(mapHeight: number, padding: CameraPadding): number {
  if (mapHeight <= 0) return 0.5;
  return (padding.top + (mapHeight - padding.bottom)) / 2 / mapHeight;
}

/**
 * True when a pin (in map-container pixels) is hidden behind the card, or
 * outside / too close to the edge of the visible area above it, so the map
 * should move. A pin comfortably inside the visible area returns false.
 */
export function isPinHiddenByCard({
  pin,
  mapSize,
  padding,
  margin = PIN_VISIBILITY_MARGIN_PX,
}: {
  pin: { x: number; y: number };
  mapSize: { width: number; height: number };
  padding: CameraPadding;
  margin?: number;
}): boolean {
  return (
    pin.x < padding.left + margin ||
    pin.x > mapSize.width - padding.right - margin ||
    pin.y < padding.top + margin ||
    pin.y > mapSize.height - padding.bottom - margin
  );
}

/**
 * `easeTo` options for a card-driven recenter (one move: center, optional zoom, padding). `retainPadding: false` keeps the
 * padding to this one move (it is not stored on the map); no `originalEvent` is
 * attached, so the move is programmatic (see moveGesture.ts) and never searches.
 */
export function getCardPanOptions(
  center: { lat: number; lng: number },
  padding: CameraPadding | undefined,
  prefersReducedMotion: boolean,
  zoom?: number
) {
  return {
    center: [center.lng, center.lat] as [number, number],
    ...(zoom !== undefined ? { zoom } : {}),
    ...(padding ? { padding, retainPadding: false } : {}),
    duration: getPanToDuration(prefersReducedMotion, zoom !== undefined),
  };
}
